import React from "react";
import {
  Palette,
  Monitor,
  Smartphone,
  Tablet,
  Laptop,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignStartVertical,
  AlignCenterVertical,
  AlignEndVertical,
} from "lucide-react";
import type { BreakpointKey, BreakpointCellConfig, BorderConfig } from "../../types";
import { BREAKPOINTS } from "../../types";
import { DEFAULT_PADDING, PADDING_OPTIONS, DEFAULT_BORDER, DEFAULT_BORDER_SIDE, BORDER_WIDTH_OPTIONS } from "../../utils/styleUtils";
import type { CellSettings } from "../UnifiedTableSettingsModal";

const breakpointIcons: Record<BreakpointKey, React.FC<{ className?: string }>> = {
  xs: Smartphone,
  sm: Tablet,
  md: Laptop,
  lg: Monitor,
  xl: Monitor,
};

interface CellSettingsTabProps {
  cellSettings: CellSettings;
  onCellSettingsChange: (settings: Partial<CellSettings>) => void;
  onApplyCellSettings: () => void;
  onClose: () => void;
  onCloseWithoutReopen: () => void;
}

const defaultCellConfig: BreakpointCellConfig = {
  backgroundColor: "",
  textColor: "",
  border: { ...DEFAULT_BORDER },
  textAlign: "left",
  verticalAlign: "middle",
  padding: { ...DEFAULT_PADDING },
  fontSize: "16",
  fontWeight: "400",
};

export const CellSettingsTab: React.FC<CellSettingsTabProps> = ({
  cellSettings,
  onCellSettingsChange,
  onApplyCellSettings,
  onClose,
  onCloseWithoutReopen,
}) => {
  // Helper to update cell breakpoint config
  const updateCellBreakpoint = (
    breakpoint: BreakpointKey,
    updates: Partial<BreakpointCellConfig>
  ) => {
    const currentStyles = cellSettings.styles || {};
    const currentConfig = currentStyles[breakpoint] || defaultCellConfig;
    onCellSettingsChange({
      styles: {
        ...currentStyles,
        [breakpoint]: { ...currentConfig, ...updates },
      },
    });
  };

  // Helper to update ALL breakpoints at once (for global settings like colors)
  const updateAllBreakpoints = (updates: Partial<BreakpointCellConfig>) => {
    const currentStyles = cellSettings.styles || {};
    const newStyles: Record<string, BreakpointCellConfig> = {};
    
    (Object.keys(BREAKPOINTS) as BreakpointKey[]).forEach((key) => {
      const currentConfig = currentStyles[key] || defaultCellConfig;
      newStyles[key] = { ...currentConfig, ...updates };
    });
    
    onCellSettingsChange({ styles: newStyles as any });
  };

  // Helper to update border for a specific side
  const updateBorderSide = (
    breakpoint: BreakpointKey,
    side: keyof BorderConfig,
    field: "color" | "width",
    value: string
  ) => {
    const currentStyles = cellSettings.styles || {};
    const currentConfig = currentStyles[breakpoint] || defaultCellConfig;
    const currentBorder = currentConfig.border || { ...DEFAULT_BORDER };
    const currentSide = currentBorder[side] || { ...DEFAULT_BORDER[side] };
    
    onCellSettingsChange({
      styles: {
        ...currentStyles,
        [breakpoint]: {
          ...currentConfig,
          border: {
            ...currentBorder,
            [side]: {
              ...currentSide,
              [field]: value,
            },
          },
        },
      },
    });
  };

  // Get current colors from xs breakpoint (used as the global value)
  const currentColors = cellSettings.styles?.xs || defaultCellConfig;

  return (
    <div className="space-y-6">
      {/* Help Section */}
      <div className="border border-blue-200 rounded-lg p-3 bg-blue-50">
        <p className="text-xs text-blue-800">
          <strong>💡 How to select cells:</strong> Click and drag across multiple cells to select them, or Shift+Click individual cells. Selected cells will have a blue highlight.
        </p>
      </div>
      
      {/* Global Colors Section */}
      <div className="border border-gray-200 rounded-lg p-4 bg-white">
        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <Palette className="w-4 h-4" />
          Colors (All Breakpoints)
        </h4>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-600">Background:</label>
            <input
              type="color"
              value={currentColors.backgroundColor || "#ffffff"}
              onChange={(e) => updateAllBreakpoints({ backgroundColor: e.target.value })}
              className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
              title="Background Color"
            />
            <input
              type="text"
              value={currentColors.backgroundColor || "#ffffff"}
              onChange={(e) => updateAllBreakpoints({ backgroundColor: e.target.value })}
              placeholder="#ffffff"
              className="w-20 border border-gray-300 px-2 py-1 rounded text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-600">Text:</label>
            <input
              type="color"
              value={currentColors.textColor || "#000000"}
              onChange={(e) => updateAllBreakpoints({ textColor: e.target.value })}
              className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
              title="Text Color"
            />
            <input
              type="text"
              value={currentColors.textColor || "#000000"}
              onChange={(e) => updateAllBreakpoints({ textColor: e.target.value })}
              placeholder="#000000"
              className="w-20 border border-gray-300 px-2 py-1 rounded text-sm"
            />
          </div>
        </div>
      </div>

      {/* Global Alignment Section */}
      <div className="border border-gray-200 rounded-lg p-4 bg-white">
        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <AlignCenter className="w-4 h-4" />
          Alignment (All Breakpoints)
        </h4>
        <div className="flex items-start gap-6 flex-wrap">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-gray-600">Horizontal:</label>
            <div className="flex gap-1">
              {([
                { value: "left", Icon: AlignLeft, label: "Left" },
                { value: "center", Icon: AlignCenter, label: "Center" },
                { value: "right", Icon: AlignRight, label: "Right" },
              ] as const).map(({ value, Icon, label }) => (
                <button
                  key={value}
                  type="button"
                  title={label}
                  onClick={() => updateAllBreakpoints({ textAlign: value })}
                  className={`flex items-center gap-1 px-2.5 py-1.5 rounded text-xs font-medium transition-all ${
                    currentColors.textAlign === value
                      ? "bg-primary-main text-white"
                      : "bg-gray-100 border border-gray-300 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-gray-600">Vertical:</label>
            <div className="flex gap-1">
              {([
                { value: "top", Icon: AlignStartVertical, label: "Top" },
                { value: "middle", Icon: AlignCenterVertical, label: "Middle" },
                { value: "bottom", Icon: AlignEndVertical, label: "Bottom" },
              ] as const).map(({ value, Icon, label }) => (
                <button
                  key={value}
                  type="button"
                  title={label}
                  onClick={() => updateAllBreakpoints({ verticalAlign: value })}
                  className={`flex items-center gap-1 px-2.5 py-1.5 rounded text-xs font-medium transition-all ${
                    currentColors.verticalAlign === value
                      ? "bg-primary-main text-white"
                      : "bg-gray-100 border border-gray-300 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Per-breakpoint Cell Settings (Borders, Alignment, Padding) */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-3">
          Per Breakpoint Settings
        </h4>
        
        <div className="space-y-3">
          {(Object.keys(BREAKPOINTS) as BreakpointKey[]).map((key) => {
            const Icon = breakpointIcons[key];
            const bp = BREAKPOINTS[key];
            const config = cellSettings.styles?.[key] || defaultCellConfig;
            
            return (
              <div key={key} className="border border-gray-200 rounded-lg p-3 bg-gray-50/50">
                {/* Breakpoint Header */}
                <div className="flex items-center gap-2 mb-2 pb-1.5 border-b border-gray-200">
                  <Icon className="w-3.5 h-3.5 text-primary-main" />
                  <span className="font-semibold text-xs text-gray-800">{key.toUpperCase()}</span>
                  <span className="text-xs text-gray-400">({bp.description})</span>
                </div>
                
                <div className="space-y-2">
                  {/* Borders Row - 4 sides */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-gray-600">Borders:</label>
                    <div className="grid grid-cols-2 gap-1.5">
                      {(["top", "right", "bottom", "left"] as const).map((side) => {
                        const border = config.border || { ...DEFAULT_BORDER };
                        const sideColor = border[side]?.color || DEFAULT_BORDER_SIDE.color;
                        const sideWidth = border[side]?.width || DEFAULT_BORDER_SIDE.width;
                        return (
                          <div key={side} className="flex items-center gap-1 bg-white p-1.5 rounded border border-gray-200">
                            <span className="text-xs text-gray-500 w-8 capitalize">{side}:</span>
                            <input
                              type="color"
                              value={sideColor}
                              onChange={(e) => updateBorderSide(key, side, "color", e.target.value)}
                              className="w-5 h-5 border border-gray-300 rounded cursor-pointer flex-shrink-0"
                              title={`${side} border color`}
                            />
                            <input
                              type="text"
                              value={sideColor}
                              onChange={(e) => updateBorderSide(key, side, "color", e.target.value)}
                              placeholder="#000"
                              className="w-14 border border-gray-300 px-1 py-0.5 rounded text-xs flex-1 min-w-0"
                              title={`${side} border hex color`}
                            />
                            <select
                              value={sideWidth}
                              onChange={(e) => updateBorderSide(key, side, "width", e.target.value)}
                              className="w-12 border border-gray-300 px-0.5 py-0.5 rounded text-xs bg-white flex-shrink-0"
                            >
                              {BORDER_WIDTH_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Alignment Row */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <label className="text-xs font-medium text-gray-600">H:</label>
                    <div className="flex gap-0.5">
                      {(["left", "center", "right"] as const).map((align) => (
                        <button
                          key={align}
                          type="button"
                          onClick={() => updateCellBreakpoint(key, { textAlign: align })}
                          className={`px-2 py-1 rounded text-xs font-medium transition-all capitalize ${
                            config.textAlign === align
                              ? "bg-primary-main text-white"
                              : "bg-white border border-gray-300 text-gray-600 hover:bg-gray-100"
                          }`}
                        >
                          {align[0].toUpperCase()}
                        </button>
                      ))}
                    </div>
                    <label className="text-xs font-medium text-gray-600 ml-2">V:</label>
                    <div className="flex gap-0.5">
                      {(["top", "middle", "bottom"] as const).map((align) => (
                        <button
                          key={align}
                          type="button"
                          onClick={() => updateCellBreakpoint(key, { verticalAlign: align })}
                          className={`px-2 py-1 rounded text-xs font-medium transition-all capitalize ${
                            config.verticalAlign === align
                              ? "bg-primary-main text-white"
                              : "bg-white border border-gray-300 text-gray-600 hover:bg-gray-100"
                          }`}
                        >
                          {align[0].toUpperCase()}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Padding Row */}
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <label className="text-xs font-medium text-gray-600">Pad:</label>
                    {(["top", "right", "bottom", "left"] as const).map((side) => (
                      <div key={side} className="flex items-center gap-0.5">
                        <span className="text-xs text-gray-400">{side[0].toUpperCase()}</span>
                        <select
                          value={config.padding?.[side] || DEFAULT_PADDING[side]}
                          onChange={(e) => updateCellBreakpoint(key, {
                            padding: { ...(config.padding || DEFAULT_PADDING), [side]: e.target.value }
                          })}
                          className="w-12 border border-gray-300 px-0.5 py-0.5 rounded text-xs bg-white"
                        >
                          {PADDING_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>

                  {/* Font Size and Weight Row */}
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex items-center gap-1.5">
                      <label className="text-xs font-medium text-gray-600">Font Size:</label>
                      <select
                        value={config.fontSize || "16"}
                        onChange={(e) => updateCellBreakpoint(key, { fontSize: e.target.value })}
                        className="w-20 border border-gray-300 px-1 py-0.5 rounded text-xs bg-white"
                      >
                        <option value="10">10px</option>
                        <option value="11">11px</option>
                        <option value="12">12px</option>
                        <option value="13">13px</option>
                        <option value="14">14px</option>
                        <option value="15">15px</option>
                        <option value="16">16px</option>
                        <option value="18">18px</option>
                        <option value="20">20px</option>
                        <option value="22">22px</option>
                        <option value="24">24px</option>
                        <option value="28">28px</option>
                        <option value="32">32px</option>
                        <option value="36">36px</option>
                        <option value="40">40px</option>
                        <option value="48">48px</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <label className="text-xs font-medium text-gray-600">Weight:</label>
                      <select
                        value={config.fontWeight || "400"}
                        onChange={(e) => updateCellBreakpoint(key, { fontWeight: e.target.value })}
                        className="w-24 border border-gray-300 px-1 py-0.5 rounded text-xs bg-white"
                      >
                        <option value="100">Thin (100)</option>
                        <option value="200">Extra Light (200)</option>
                        <option value="300">Light (300)</option>
                        <option value="400">Normal (400)</option>
                        <option value="500">Medium (500)</option>
                        <option value="600">Semi Bold (600)</option>
                        <option value="700">Bold (700)</option>
                        <option value="800">Extra Bold (800)</option>
                        <option value="900">Black (900)</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex gap-2 justify-end pt-4 border-t">
        <button
          type="button"
          onClick={() => {
            onCloseWithoutReopen();
            onApplyCellSettings();
          }}
          className="px-4 py-2 bg-primary-main text-white rounded-md text-sm font-medium hover:bg-primary-main/90"
        >
          Apply to Selected Cells
        </button>
      </div>
    </div>
  );
};

